import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Pencil, Plus, Save, Search, Trash2 } from "lucide-react";

const empty = {
  razao_social: "",
  nome_fantasia: "",
  cnpj: "",
  ie: "",
  endereco: "",
  cep: "",
  cidade: "",
  uf: "",
  email: "",
  telefone: "",
};

export default function Emitente() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/emitentes");
    setItems(data || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = items.filter((item) => {
    const term = q.trim().toLowerCase();
    if (!term) return true;
    return [
      item.razao_social,
      item.nome_fantasia,
      item.cnpj,
      item.email,
      item.cidade,
      item.uf,
    ].some((value) => String(value || "").toLowerCase().includes(term));
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm(empty);
    setEditId(null);
  };

  const edit = (item) => {
    setForm({ ...empty, ...item });
    setEditId(item.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/emitentes/${editId}`, form);
      else await api.post("/emitentes", form);
      toast.success(editId ? "Emitente atualizado" : "Emitente cadastrado");
      setOpen(false);
      reset();
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar emitente";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const del = (item) => {
  toast(`Excluir emitente ${item.razao_social}?`, {
    description: "Essa ação não poderá ser desfeita.",
    duration: Infinity,
    action: {
      label: "Excluir",
      onClick: async () => {
        try {
          await api.delete(`/emitentes/${item.id}`);
          toast.success("Emitente excluído com sucesso");
          load();
        } catch {
          toast.error("Erro ao excluir o emitente");
        }
      },
    },
    cancel: { label: "Cancelar" },
  });
};

  return (
    <div className="space-y-6" data-testid="emitente-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Configuração</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Emitentes</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Empresas do grupo que emitem boletos e podem ter contas Pix monitoradas no Hub.
          </p>
        </div>

        <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) reset(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="emitente-new-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo emitente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{editId ? "Editar emitente" : "Novo emitente"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Razão Social" required value={form.razao_social} onChange={update("razao_social")} testid="emit-razao" className="sm:col-span-2" />
              <Field label="Nome Fantasia" value={form.nome_fantasia} onChange={update("nome_fantasia")} testid="emit-fantasia" />
              <Field label="CNPJ" required value={form.cnpj} onChange={update("cnpj")} mono testid="emit-cnpj" />
              <Field label="Inscrição Estadual" value={form.ie} onChange={update("ie")} mono testid="emit-ie" />
              <Field label="E-mail" type="email" value={form.email} onChange={update("email")} testid="emit-email" />
              <Field label="Telefone" value={form.telefone} onChange={update("telefone")} mono testid="emit-telefone" />
              <Field label="CEP" value={form.cep} onChange={update("cep")} mono testid="emit-cep" />
              <Field label="Endereço" value={form.endereco} onChange={update("endereco")} testid="emit-endereco" className="sm:col-span-2" />
              <Field label="Cidade" value={form.cidade} onChange={update("cidade")} testid="emit-cidade" />
              <Field label="UF" value={form.uf} onChange={update("uf")} mono testid="emit-uf" />
              <DialogFooter className="sm:col-span-2">
                <Button disabled={busy} className="rounded-sm" data-testid="emit-save">
                  <Save className="w-4 h-4 mr-2" />
                  {busy ? "Salvando..." : "Salvar emitente"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 text-orange-600 shrink-0" />
            <div>
              <div className="font-display font-semibold">Empresas emitentes</div>
              <p className="text-xs text-zinc-500 mt-0.5">A matriz Pix da Administração usa esta lista.</p>
            </div>
          </div>
          <Badge variant="outline" className="rounded-sm font-mono text-[10px] uppercase">
            {items.length} emitentes
          </Badge>
        </div>
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Buscar por razão social, CNPJ, e-mail ou cidade..."
            className="rounded-sm border-0 shadow-none focus-visible:ring-0 px-0"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="emitente-search"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="tbl" data-testid="emitentes-table">
            <thead>
              <tr>
                <th>Razão Social</th>
                <th>CNPJ</th>
                <th>E-mail</th>
                <th>Cidade/UF</th>
                <th className="w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-sm text-zinc-500">
                    Nenhum emitente cadastrado.
                  </td>
                </tr>
              )}
              {filtered.map((item) => (
                <tr key={item.id} data-testid={`emitente-row-${item.id}`}>
                  <td>
                    <div className="font-medium">{item.razao_social}</div>
                    <div className="text-[11px] text-zinc-500">{item.nome_fantasia || "-"}</div>
                  </td>
                  <td className="font-mono text-xs">{item.cnpj}</td>
                  <td className="text-zinc-600">{item.email || "-"}</td>
                  <td className="text-zinc-600">{item.cidade ? `${item.cidade}/${item.uf || ""}` : "-"}</td>
                  <td className="text-right">
                    <button onClick={() => edit(item)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`emitente-edit-${item.id}`} title="Editar">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => del(item)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm" data-testid={`emitente-delete-${item.id}`} title="Excluir">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, mono, className = "", testid, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Input {...props} className={`rounded-sm h-10 ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
    </div>
  );
}
